/**
 * WebSocket client for online multiplayer.
 * Sends local player inputs, receives remote player state.
 */
export class NetworkClient {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.playerId = null;  // 0 or 1, assigned by server
    this.roomId = null;

    // Callbacks
    this.onMessage = null;
    this.onConnect = null;
    this.onDisconnect = null;         // unexpected socket drop
    this.onOpponentDisconnect = null;  // opponent left (server message)
    this.onRoomJoined = null;
    this.onOpponentJoined = null;
    this.onGameStart = null;
    this.onError = null;

    this._intentionalClose = false;
  }

  connect(serverUrl, roomCode = '') {
    return new Promise((resolve, reject) => {
      this._intentionalClose = false;

      try {
        const url = roomCode
          ? `${serverUrl}?room=${encodeURIComponent(roomCode)}`
          : serverUrl;
        this.ws = new WebSocket(url);
      } catch (e) {
        reject(new Error('Invalid server URL. Check the address and try again.'));
        return;
      }

      // Timeout — if no connection after 8 seconds, give up
      const timeout = setTimeout(() => {
        if (!this.connected) {
          this._intentionalClose = true;
          this.ws.close();
          reject(new Error('Connection timed out. The server may be offline.'));
        }
      }, 8000);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this.connected = true;
        if (this.onConnect) this.onConnect();
        resolve();
      };

      this.ws.onclose = () => {
        clearTimeout(timeout);
        const wasConnected = this.connected;
        this.connected = false;
        // Only fire onDisconnect for unexpected closes (not user-initiated)
        if (!this._intentionalClose && wasConnected) {
          if (this.onDisconnect) this.onDisconnect();
        }
      };

      this.ws.onerror = (e) => {
        clearTimeout(timeout);
        if (this.onError) this.onError(e);
        if (!this.connected) {
          reject(new Error('Could not reach the server. Check the URL or try again later.'));
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          this._handleMessage(msg);
        } catch (e) {
          console.error('Failed to parse message:', e);
        }
      };
    });
  }

  _handleMessage(msg) {
    switch (msg.type) {
      case 'room-joined':
        this.playerId = msg.playerId;
        this.roomId = msg.roomId;
        if (this.onRoomJoined) this.onRoomJoined(msg);
        break;

      case 'opponent-joined':
        if (this.onOpponentJoined) this.onOpponentJoined(msg);
        break;

      case 'game-start':
        if (this.onGameStart) this.onGameStart(msg);
        break;

      case 'opponent-disconnected':
        // Distinct from socket close — the opponent left, not us
        if (this.onOpponentDisconnect) this.onOpponentDisconnect();
        break;

      case 'error':
        console.warn('Server error:', msg.message);
        if (this.onError) this.onError(new Error(msg.message));
        break;

      default:
        if (this.onMessage) this.onMessage(msg);
        break;
    }
  }

  /** Send local player state to server */
  sendState(state) {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      type: 'player-state',
      state
    }));
  }

  /** Send an action (rock throw, scissors swing, etc.) */
  sendAction(action) {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      type: 'player-action',
      action
    }));
  }

  /** Send game event (round won, etc.) */
  sendEvent(event) {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      type: 'game-event',
      event
    }));
  }

  disconnect() {
    this._intentionalClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
  }
}
